/**
 * Free-tool usage telemetry. Mirrors the gtag-gated pattern in
 * `home-analytics.ts` / `pricing-analytics.ts`: a no-op during SSR /
 * build-time prerender or when analytics is disabled (no `window.gtag`).
 *
 * Feeds growth lever ⑥ (measurement): a tool page's value is not pageviews but
 * whether visitors actually *run* it and then click through to the product, so
 * conversion, copy and download are tracked separately from the CTA.
 */

import { emitGaEvent } from "@/lib/ga-event";
import { isBlockingSetupNote } from "@/lib/import-normalize";
import type { ConversionNote } from "@/lib/import-normalize";

/**
 * Stable tool/action/preset event contract (w1/m95/t002).
 *
 * Event name: `tool_use`. Every emission carries `event_category:
 * "engagement"`, `source: "tools"` and `event_label: "{tool}_{action}"` —
 * three tools times four actions, twelve stable labels. Labels are never
 * renamed: the conversion report and any historical GA data join on them.
 *
 * Reporting interpretation (for the conversion report, w1/m95/t003–t004):
 *
 * - `convert` counts distinct inputs that produced output, not keystrokes,
 *   sessions, unique visitors, imports or ordered funnel steps. Editing the
 *   input and converting again emits again, so repeated edits legitimately
 *   produce multiple events and the count can exceed page views.
 * - `copy` / `download` count output-takeaway clicks carrying the same bounded
 *   details; `cta` counts product-link clicks. None of them implies the
 *   visitor signed up or completed an import.
 * - `preset` distinguishes bank-lander uses (the lander's registry slug) from
 *   generic-tool uses (param absent). The bank routes validate the slug
 *   against the preset registry before rendering, so only known slugs travel.
 * - Locale is not a param: it derives from the page path at report time.
 * - No new params were needed for reporting: `preset`, `output_mode` and
 *   `result` already cover the by-tool/preset/outcome breakdowns.
 *
 * GA availability: `preset`, `output_mode` and `result` must each be
 * registered as an event-scoped custom dimension before they appear in
 * reports; until then rows arrive without them (absent, never zero-filled).
 *
 * Privacy bound: only the enums below travel. Never pasted input text,
 * filenames, account names, transaction descriptions, amounts or currencies.
 */

/** Which tool the event came from — becomes part of the GA event label. */
export type ToolName =
  | "csv_to_beancount"
  | "ledger_to_beancount"
  | "ofx_to_beancount";

/**
 * What the visitor did. `convert` fires once per distinct input that produced
 * output, not per keystroke, so the count is inputs converted rather than
 * typing volume. It is not a unique-session counter, and the four actions are
 * independent tallies, not steps in an ordered funnel.
 */
type ToolAction = "convert" | "copy" | "download" | "cta";

/**
 * Bounded result metadata threaded through `tool_use` events.
 *
 * Only small enums travel: the output artifact selected and whether the input
 * converted fully, partially, not at all, or was blocked by configuration.
 * Never CSV text, filenames, account names, descriptions, amounts or
 * statement balances/dates.
 */
export interface ToolUseDetails {
  /**
   * Which artifact the action applied to, when the tool offers a choice.
   * Mirrors `CsvOutputMode` on the CSV converter; OFX/Ledger send the same
   * pair for their opens toggle (with opens is standalone `complete`,
   * without is `append`), so the outcome breakdown covers every tool.
   */
  outputMode?: "complete" | "append";
  /**
   * Whether the input converted fully, partially, not at all, or was blocked.
   * `blocked` (configuration errors), `empty` (nothing converted), `partial`
   * (some records skipped), `complete`. Every converter sends this on the
   * convert/copy/download events that carry output.
   */
  result?: "complete" | "partial" | "empty" | "blocked";
}

/**
 * Classify an OFX/Ledger conversion into the bounded `result` vocabulary,
 * from the artifact actually acted on: the notes rendered beside the output
 * and its converted count. Mirrors `ConverterOutput`'s own blocked/partial
 * split, so the event and the panel can never disagree: a misconfigured
 * account or currency blocks the whole run (`blocked`), anything else that
 * converted nothing is `empty`, converted output beside error-severity notes
 * is `partial`, and warnings alone never demote a clean run.
 *
 * Returns only the enum — note text, accounts and amounts never travel.
 */
export function classifyConverterResult(
  notes: readonly ConversionNote[],
  converted: number,
): NonNullable<ToolUseDetails["result"]> {
  if (notes.some(isBlockingSetupNote)) return "blocked";
  if (converted === 0) return "empty";
  if (notes.some((note) => note.severity === "error")) return "partial";
  return "complete";
}

/**
 * Build the convert/copy/download outcome pair for an opens-toggle
 * converter (OFX, Ledger): the toggle is the complete/append artifact
 * choice, and the result is classified from the notes rendered beside the
 * output so the event and the panel can never disagree.
 */
export function converterOutcomeDetails(
  withOpens: boolean,
  notes: readonly ConversionNote[],
  converted: number,
): ToolUseDetails {
  return {
    outputMode: withOpens ? "complete" : "append",
    result: classifyConverterResult(notes, converted),
  };
}

/**
 * Emit a GA `tool_use` event tagged with the tool and the action.
 *
 * `preset` names the bank lander the use came from, when it came from one. It
 * is an extra param rather than a different event, so the existing reports keep
 * working while "which banks' switchers actually convert" becomes answerable.
 * `details` carries only the bounded `outputMode`/`result` enums described
 * above; event counts are inputs converted, not sessions.
 */
export function trackToolUse(
  tool: ToolName,
  action: ToolAction,
  preset?: string,
  details?: ToolUseDetails,
): void {
  emitGaEvent("tool_use", {
    event_category: "engagement",
    event_label: `${tool}_${action}`,
    source: "tools",
    ...(preset ? { preset } : {}),
    ...(details?.outputMode ? { output_mode: details.outputMode } : {}),
    ...(details?.result ? { result: details.result } : {}),
  });
}

/**
 * Bounded CSV profile-action vocabulary (w1/m97/t003). A separate event from
 * `tool_use` because none of the four conversion actions expresses these,
 * and the twelve `tool_use` labels stay frozen for the conversion report.
 *
 * - `save` counts explicit saves, including overwrites of the selected
 *   profile (Update) — one counter for "the reader kept this setup".
 * - `load` counts clean applies: the profile resolved against the current
 *   file with nothing for the reader to review.
 * - `delete` counts explicit removals.
 * - `mismatch` counts load attempts that needed review (missing/duplicate
 *   headers, or a profile saved under another preset). It replaces `load`,
 *   so `load` stays a clean-apply count and header drift is observable.
 *
 * Reporting interpretation: these are **action counts**, not configurations,
 * readers or sessions — saving twice emits twice, and a mismatch still
 * applies the resolvable columns while asking for review.
 *
 * Privacy bound: only the `action` enum and the optional `preset` slug (the
 * same bounded registry value `tool_use` already carries) travel. Never
 * profile ids or names, header names, account configuration, currencies,
 * amounts, statement balances/dates or CSV text. With analytics disabled (no
 * `window.gtag`) this is a no-op via `emitGaEvent`, like every other reporter
 * here.
 */
/**
 * Bounded CSV category-review outcome vocabulary (w1/m106/t004). A separate
 * event from `tool_use` because none of the four conversion actions expresses
 * these, and the twelve `tool_use` labels stay frozen for the conversion
 * report.
 *
 * - `mapped` counts converted inputs where every categorized row posts to an
 *   explicitly assigned account — the success case, nothing falls back.
 * - `fallback` counts converted inputs where at least one row falls back to
 *   the counter account and is shown for review.
 *
 * Reporting interpretation: these are **action counts**, not readers, sessions
 * or completed imports — converting again emits again, and assigning the last
 * fallback flips a later emission from `fallback` to `mapped` for the same
 * input, so the pair shows whether review resolves fallbacks.
 *
 * Privacy bound: only the `action` enum and the optional `preset` slug (the
 * same bounded registry value `tool_use` already carries) travel. Never
 * category values, account names, header names, amounts, currencies or CSV
 * text. With analytics disabled (no `window.gtag`) this is a no-op via
 * `emitGaEvent`, like every other reporter here.
 */
type CsvCategoryAction = "mapped" | "fallback";

export function trackCsvCategoryAction(
  action: CsvCategoryAction,
  preset?: string | null,
): void {
  emitGaEvent("csv_category", {
    event_category: "engagement",
    source: "tools",
    action,
    ...(preset ? { preset } : {}),
  });
}

/**
 * Portable-file outcomes (w1/m196/t003) extend the same `csv_profile` event
 * additively; `save`/`load`/`delete`/`mismatch` keep their meaning (a file
 * apply never emits `load`/`mismatch`, so those stay "reused a saved
 * profile").
 *
 * - `export` counts completed downloads, with the bounded `scope` enum:
 *   `current` (active configuration), `selected` (one saved profile) or
 *   `all` (the saved collection). An export refused by validation emits
 *   nothing.
 * - `import_valid` / `import_refused` count chosen files that passed or
 *   failed all-or-nothing validation. A dismissed chooser emits nothing.
 * - `import_saved` counts save-choice commits the store accepted with at
 *   least one profile added or replaced; cancel, all-skip and blocked/full
 *   storage emit nothing.
 * - `import_apply` counts explicit Apply clicks on a staged file profile.
 *
 * Action counts, not files, readers or completed accounting imports. The
 * payload never carries profile ids, labels, headers, accounts, categories,
 * rule patterns, file names, sizes or contents.
 */
type CsvProfileAction =
  | "save"
  | "load"
  | "delete"
  | "mismatch"
  | "export"
  | "import_valid"
  | "import_refused"
  | "import_saved"
  | "import_apply";

export type CsvProfileExportScope = "current" | "selected" | "all";

export function trackCsvProfileAction(
  action: CsvProfileAction,
  preset?: string | null,
  scope?: CsvProfileExportScope,
): void {
  emitGaEvent("csv_profile", {
    event_category: "engagement",
    source: "tools",
    action,
    ...(preset ? { preset } : {}),
    ...(action === "export" && scope ? { scope } : {}),
  });
}

/**
 * Bounded CSV split-review vocabulary. A separate event from
 * `tool_use` because none of the four conversion actions expresses a split,
 * and the twelve `tool_use` labels stay frozen for the conversion report.
 *
 * - `start` counts explicit "Split row" clicks — a reader opened a review.
 * - `reset` / `remove` count explicit restart and discard clicks.
 * - `copy` / `download` count **valid split artifacts** taken away: the
 *   output carried at least one applied split on a selected row, and a split
 *   that does not balance keeps both actions disabled, so these are never
 *   blocked exports. They ride *beside* the unchanged `tool_use`
 *   copy/download event for the same click, which remains the one takeaway
 *   count — sum `tool_use`, never `tool_use` + `csv_split`.
 *
 * Automatic recalculation, rendering and allocation keystrokes emit nothing:
 * only explicit clicks reach this reporter. Reporting interpretation: these
 * are **action counts**, not readers, sessions, imports or split rows —
 * starting two splits emits twice.
 *
 * Privacy bound: only the `action` enum, the optional `preset` slug (the
 * same bounded registry value `tool_use` carries) and, on takeaways, the
 * bounded `output_mode` enum travel. Never row indexes or line numbers,
 * descriptions, accounts, amounts, currencies, allocation counts or CSV
 * text. With analytics disabled this is a no-op via `emitGaEvent`.
 */
export type CsvSplitAction = "start" | "reset" | "remove" | "copy" | "download";

export function trackCsvSplitAction(
  action: CsvSplitAction,
  preset?: string | null,
  outputMode?: ToolUseDetails["outputMode"],
): void {
  emitGaEvent("csv_split", {
    event_category: "engagement",
    source: "tools",
    action,
    ...(preset ? { preset } : {}),
    ...((action === "copy" || action === "download") && outputMode
      ? { output_mode: outputMode }
      : {}),
  });
}

/**
 * Bounded QIF split vocabulary for the OFX/QIF converter. A
 * separate event from `tool_use` because none of the four conversion actions
 * expresses a split review, and the twelve `tool_use` labels stay frozen for
 * the conversion report.
 *
 * - `accept` / `accept_all` count explicit clicks on one suggested split
 *   account or on "use all suggestions".
 * - `map` counts a split-account field the reader edited by hand and left
 *   holding a valid destination (emitted on leaving the field, never per
 *   keystroke).
 * - `copy` / `download` count **valid split artifacts** taken away: the
 *   output carried at least one fully mapped split transaction. Unmapped
 *   split records never reach the output, so these are never flattened or
 *   blocked exports. They ride *beside* the unchanged `tool_use`
 *   copy/download event for the same click, which remains the one takeaway
 *   count — sum `tool_use`, never `tool_use` + `qif_split`. They carry the
 *   same bounded `output_mode`/`result` pair as that event, so a partial
 *   split artifact stays distinguishable from a complete one.
 *
 * Reporting interpretation: **action counts**, not readers, sessions,
 * imports, categories or split rows.
 *
 * Privacy bound: only these enums travel. Never category names, account
 * names or ids, amounts, currencies, payees, memos, counts or file content.
 * With analytics disabled this is a no-op via `emitGaEvent`.
 */
export type QifSplitAction =
  | "accept"
  | "accept_all"
  | "map"
  | "copy"
  | "download";

export function trackQifSplitAction(
  action: QifSplitAction,
  details?: ToolUseDetails,
): void {
  const takeaway = action === "copy" || action === "download";
  emitGaEvent("qif_split", {
    event_category: "engagement",
    source: "tools",
    action,
    ...(takeaway && details?.outputMode
      ? { output_mode: details.outputMode }
      : {}),
    ...(takeaway && details?.result ? { result: details.result } : {}),
  });
}
